import { gql } from "@apollo/client";
import {
  BUTTON_EXTERNAL_RECORD,
  BUTTON_RECORD,
  DOWNLOAD_BUTTON_RECORD,
  ARTICLE_FIELDS,
} from "../shared";
import { SERVICE_FIELDS } from "./services";
import { QUOTE } from "./quote";
import { HUBSPOT_BUTTON_RECORD } from "./button";

// Inline fragments for single-use cases
const OFFICE_FIELDS = gql`
  fragment OfficeRecordFields on OfficeRecord {
    id
    slug
    title
    shortTitle
    intro {
      value
    }
    parent {
      slug
      parent {
        slug
        parent {
          slug
          parent {
            slug
          }
        }
      }
    }
  }
`;

export const EXPERTISE_FIELDS = gql`
  fragment ExpertiseFields on ExpertiseRecord {
    id
    slug
    title
    shortTitle
    intro {
      value
    }
    shortIntro {
      value
    }
    # Add titles and slugs for AppContext
    titles: _allTitleLocales {
      locale
      value
    }
    slugs: _allSlugLocales {
      locale
      value
    }
  }
`;

export const EXPERTISE_OVERVIEW_META = gql`
  fragment ExpertiseOverviewMeta on ExpertisesOverviewRecord {
    titles: _allTitleLocales {
      locale
      value
    }
    slugs: _allSlugLocales {
      locale
      value
    }
    seoMetaTags: _seoMetaTags(locale: $locale) {
      attributes
      content
      tag
    }
    blockSearchIndexing
  }
`;

export const FOR_WHO_OVERVIEW_META = gql`
  fragment ForWhoOverviewMeta on ForWhoOverviewRecord {
    titles: _allTitleLocales {
      locale
      value
    }
    slugs: _allSlugLocales {
      locale
      value
    }
  }
`;

export const TARGET_GROUP_FIELDS = gql`
  fragment TargetGroupFields on TargetGroupRecord {
    id
    slug
    title
    shortTitle
    intro {
      value
    }
    shortIntro {
      value
    }
    parent {
      slug
    }
    # Add titles and slugs for AppContext
    titles: _allTitleLocales {
      locale
      value
    }
    slugs: _allSlugLocales {
      locale
      value
    }
  }
`;

export const AUDIT_PAGE_FIELDS = gql`
  fragment AuditPageFields on AuditPageRecord {
    id
    slug
    title
    shortTitle
    intro {
      value
    }
    # Add titles and slugs for AppContext
    titles: _allTitleLocales {
      locale
      value
    }
    slugs: _allSlugLocales {
      locale
      value
    }
  }
`;

export const TAXES_OVERVIEW_META = gql`
  fragment TaxesOverviewMeta on TaxesOverviewRecord {
    titles: _allTitleLocales {
      locale
      value
    }
    slugs: _allSlugLocales {
      locale
      value
    }
  }
`;

export const RICHTEXT_SECTION_QUERY = gql`
  ${BUTTON_RECORD}
  ${BUTTON_EXTERNAL_RECORD}
  ${DOWNLOAD_BUTTON_RECORD}
  ${HUBSPOT_BUTTON_RECORD}
  fragment RichtextSectionQuery on RichTextSectionRecord {
    id
    __typename
    enableBackground
    structuredText {
      value
      blocks {
        __typename
        ... on ButtonRecord {
          ...ButtonBlock
        }
        ... on ButtonExternalRecord {
          ...ButtonExternalBlock
        }
        ... on DownloadButtonRecord {
          ...DownloadButtonBlock
        }
        ... on HubspotButtonRecord {
          ...HubspotButtonBlock
        }
        ... on ImageRecord {
          id
          fullWidth
          image {
            id
            focalPoint {
              x
              y
            }
            responsiveImage(
              imgixParams: {
                auto: format
                fit: crop
                crop: focalpoint
                w: "800"
                h: "500"
                q: "75"
              }
            ) {
              alt
              height
              src
              title
              width
            }
            responsiveImageFullWidth: responsiveImage(
              imgixParams: {
                auto: format
                fit: crop
                crop: focalpoint
                w: "1560"
                h: "500"
                q: "75"
              }
            ) {
              alt
              height
              src
              title
              width
            }
          }
        }
        ... on VideoRecord {
          id
          author
          authorFunction
          video {
            height
            width
            thumbnailUrl
            title
            url
          }
        }
        ... on HighlightedTextRecord {
          id
          text {
            value
          }
          color
        }
        ... on TableRecord {
          id
          table
        }
      }
      links {
        __typename
        ... on ReusableTextRecord {
          id
          content
        }
        ... on ArticleRecord {
          id
          slug
        }
        ... on AccountanciesOverviewRecord {
          id
          __typename
        }
        ... on AccountancyRecord {
          id
          slug
        }
        ... on ArticlesOverviewRecord {
          id
          slug
        }
        ... on AuditPageRecord {
          id
          slug
        }
        ... on ContactRecord {
          id
          slug
        }
        ... on ExpertiseRecord {
          id
          slug
        }
        ... on ExpertisesOverviewRecord {
          id
          slug
        }
        ... on ForWhoOverviewRecord {
          id
          slug
        }
        ... on HomepageRecord {
          id
          slug
        }
        ... on JobListingOverviewRecord {
          id
          slug
        }
        ... on NearbyOverviewRecord {
          id
          slug
        }
        ... on OfficeRecord {
          id
          slug
          parent {
            slug
            parent {
              slug
              parent {
                slug
                parent {
                  slug
                }
              }
            }
          }
        }
        ... on PageRecord {
          id
          slug
        }
        ... on ServiceRecord {
          id
          slug
          parent {
            slug
          }
        }
        ... on ServicesOverviewRecord {
          id
          slug
        }
        ... on TaxesOverviewRecord {
          id
          slug
        }
        ... on TargetGroupRecord {
          id
          slug
          parent {
            slug
          }
        }
      }
    }
  }
`;

export const ACCORDION_RECORD = gql`
  fragment AccordionRecord on AccordionRecord {
    id
    timeline
    items {
      icon {
        id
        iconName
      }
      id
      title
      content {
        value
      }
    }
  }
`;

export const ACCORDION_SECTION_QUERY = gql`
  ${ACCORDION_RECORD}
  fragment AccordionSectionQuery on AccordionSectionRecord {
    id
    __typename
    title
    pretitle
    accordion {
      ...AccordionRecord
    }
    enableBackground
  }
`;

export const CTA_SECTION_QUERY = gql`
  ${BUTTON_RECORD}
  ${BUTTON_EXTERNAL_RECORD}
  ${DOWNLOAD_BUTTON_RECORD}
  ${HUBSPOT_BUTTON_RECORD}
  fragment CtaSectionQuery on CtaSectionRecord {
    id
    __typename
    title
    text {
      value
    }
    button {
      ... on ButtonExternalRecord {
        ...ButtonExternalBlock
      }
      ... on DownloadButtonRecord {
        ...DownloadButtonBlock
      }
      ... on ButtonRecord {
        ...ButtonBlock
      }
      ... on HubspotButtonRecord {
        ...HubspotButtonBlock
      }
    }
    enableBackground
  }
`;

export const PEOPLE_SECTION_QUERY = gql`
  fragment PeopleSectionQuery on PeopleSectionRecord {
    id
    __typename
    title
    layout
    people {
      id
      image {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "100", h: "100", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
      linkedin
      name
      phone
      role
      calendly
      subpage {
        ... on OfficeRecord {
          slug
          parent {
            slug
            parent {
              slug
              parent {
                slug
                parent {
                  slug
                }
              }
            }
          }
        }
      }
    }
    enableBackground
  }
`;

export const TEASER_RECORD = gql`
  ${BUTTON_RECORD}
  ${BUTTON_EXTERNAL_RECORD}
  ${DOWNLOAD_BUTTON_RECORD}
  ${HUBSPOT_BUTTON_RECORD}
  fragment TeaserBlock on TeaserRecord {
    id
    icon {
      iconName
    }
    pretitle
    title
    description {
      value
    }
    button {
      ...ButtonBlock
      ...ButtonExternalBlock
      ...DownloadButtonBlock
      ...HubspotButtonBlock
    }
  }
`;

export const CAROUSEL_SECTION_QUERY = gql`
  ${SERVICE_FIELDS}
  ${OFFICE_FIELDS}
  ${EXPERTISE_FIELDS}
  ${BUTTON_RECORD}
  ${DOWNLOAD_BUTTON_RECORD}
  ${HUBSPOT_BUTTON_RECORD}
  ${TARGET_GROUP_FIELDS}
  ${TEASER_RECORD}
  ${ARTICLE_FIELDS}
  fragment CarouselSectionQuery on CarouselSectionRecord {
    id
    pretitle
    title
    description {
      value
    }
    button {
      ...ButtonBlock
      ...DownloadButtonBlock
      ...HubspotButtonBlock
    }
    linkedTeasers {
      __typename
      ... on ServiceRecord {
        ...ServiceFields
        introImage {
          id
          responsiveImage(
            imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
          ) {
            alt
            height
            src
            title
            width
          }
        }
      }
      ... on ArticleRecord {
        ...ArticleFields
        introImage {
          id
          responsiveImage(
            imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
          ) {
            alt
            height
            src
            title
            width
          }
        }
      }
      ... on OfficeRecord {
        ...OfficeRecordFields
        introImage {
          id
          responsiveImage(
            imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
          ) {
            alt
            height
            src
            title
            width
          }
        }
      }
      ... on ExpertiseRecord {
        ...ExpertiseFields
        introImage {
          id
          responsiveImage(
            imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
          ) {
            alt
            height
            src
            title
            width
          }
        }
      }
      ... on TargetGroupRecord {
        ...TargetGroupFields
        introImage {
          id
          responsiveImage(
            imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
          ) {
            alt
            height
            src
            title
            width
          }
        }
        parent {
          slug
        }
      }
    }
    teasers {
      __typename
      ... on TeaserRecord {
        ...TeaserBlock
      }
    }
    enableBackground
  }
`;

export const QUOTE_CAROUSEL_SECTION_QUERY = gql`
  ${QUOTE}
  fragment QuoteCarouselSectionQuery on QuoteCarouselSectionRecord {
    id
    quotes {
      ...Quote
    }
    enableBackground
  }
`;

export const CONTACT_FORM_SECTION_QUERY = gql`
  fragment ContactFormSectionQuery on ContactFormSectionRecord {
    id
    title
    intro
    formType
    anchor
    enableBackground
  }
`;

export const AIRTABLE_FORM_SECTION_QUERY = gql`
  fragment AirtableFormSectionQuery on AirtableFormSectionRecord {
    id
    formId
    enableBackground
  }
`;

export const CAMPAIGN_FORM_SECTION_QUERY = gql`
  fragment CampaignFormSectionQuery on CampaignFormSectionRecord {
    id
    dates {
      date
    }
    disablePartnershipEnterprise
    enableBackground
  }
`;

export const IMAGE_SQUARE_RECORD = gql`
  fragment ImageSquareBlock on ImageRecord {
    id
    image {
      id
      focalPoint {
        x
        y
      }
      responsiveImage(
        imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "760", h: "760", q: "75" }
      ) {
        alt
        height
        src
        title
        width
      }
    }
  }
`;

export const VIDEO_RECORD = gql`
  fragment VideoRecord on VideoRecord {
    id
    video {
      height
      width
      thumbnailUrl
      title
      url
    }
    author
    authorFunction
  }
`;

export const EMBED_SECTION_QUERY = gql`
  fragment EmbedSectionQuery on EmbedSectionRecord {
    id
    embed
    enableBackground
  }
`;

export const STATISTIC_RECORD = gql`
  fragment StatisticRecord on StatisticRecord {
    id
    pretitle
    title
    text {
      value
    }
  }
`;

export const CHECKLISTITEM_RECORD = gql`
  fragment ChecklistItemRecord on ChecklistItemRecord {
    id
    title
    text {
      value
    }
  }
`;

export const BRANDED_IMAGE_TEASER_RECORD = gql`
  ${BUTTON_RECORD}
  ${BUTTON_EXTERNAL_RECORD}
  ${DOWNLOAD_BUTTON_RECORD}
  ${HUBSPOT_BUTTON_RECORD}
  fragment BrandedImageTeaserRecord on BrandedImageTeaserRecord {
    button {
      ...ButtonBlock
      ...ButtonExternalBlock
      ...DownloadButtonBlock
      ...HubspotButtonBlock
    }
    id
    email
    chatUrl
    phoneNumber
    title
    descriptionRich {
      blocks
      links
      value
    }
    image {
      id
      responsiveImage(
        imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "400", h: "400", q: "75" }
      ) {
        alt
        height
        src
        title
        width
      }
    }
  }
`;

export const GRID_SECTION_QUERY = gql`
  ${TEASER_RECORD}
  ${IMAGE_SQUARE_RECORD}
  ${ACCORDION_SECTION_QUERY}
  ${QUOTE}
  ${VIDEO_RECORD}
  ${CONTACT_FORM_SECTION_QUERY}
  ${STATISTIC_RECORD}
  ${CHECKLISTITEM_RECORD}
  ${BRANDED_IMAGE_TEASER_RECORD}
  fragment GridSectionQuery on GridSectionRecord {
    id
    pretitle
    title
    description {
      value
    }
    columnCount
    border
    hoverEffect
    items {
      __typename
      ... on ImageRecord {
        ...ImageSquareBlock
      }
      ... on TeaserRecord {
        ...TeaserBlock
      }
      ... on AccordionRecord {
        ...AccordionRecord
      }
      ... on QuoteRecord {
        ...Quote
      }
      ... on VideoRecord {
        ...VideoRecord
      }
      ... on StatisticRecord {
        ...StatisticRecord
      }
      ... on ChecklistItemRecord {
        ...ChecklistItemRecord
      }
      ... on BrandedImageTeaserRecord {
        ...BrandedImageTeaserRecord
      }
    }
    enableBackground
  }
`;

export const TEXT_MEDIA_COLUMN_SECTION_QUERY = gql`
  ${IMAGE_SQUARE_RECORD}
  ${QUOTE_CAROUSEL_SECTION_QUERY}
  ${BUTTON_RECORD}
  ${BUTTON_EXTERNAL_RECORD}
  ${DOWNLOAD_BUTTON_RECORD}
  ${HUBSPOT_BUTTON_RECORD}
  fragment TextMediaColumnSectionQuery on TextMediaColumnSectionRecord {
    id
    pretitle
    title
    text {
      value
    }
    button {
      ...ButtonBlock
      ...ButtonExternalBlock
      ...DownloadButtonBlock
      ...HubspotButtonBlock
    }
    alignment
    width
    media {
      __typename
      ... on ImageRecord {
        ...ImageSquareBlock
      }
      ... on QuoteCarouselSectionRecord {
        ...QuoteCarouselSectionQuery
      }
      ... on AccordionRecord {
        ...AccordionRecord
      }
    }
    enableBackground
  }
`;

export const GALLERY_SECTION_QUERY = gql`
  ${IMAGE_SQUARE_RECORD}
  fragment GallerySectionQuery on GallerySectionRecord {
    id
    title
    images {
      __typename
      ... on ImageRecord {
        ...ImageSquareBlock
      }
    }
    enableBackground
  }
`;

export const LATEST_NEWS_SECTION_QUERY = gql`
  fragment LatestNewsSectionQuery on LatestNewsSectionRecord {
    id
    __typename
    title
    enableBackground
  }
`;

export const HIGHLIGHTED_JOBS_SECTION_QUERY = gql`
  fragment HighlightedJobsSectionQuery on HighlightedJobsSectionRecord {
    id
    __typename
    title
    pretitle
    background
  }
`;

export const HIGHLIGHTS_SECTION_QUERY = gql`
  fragment HighlightsSectionQuery on HighlightsSectionRecord {
    id
    title
    highlights {
      __typename
      ... on ExpertiseRecord {
        slug
        id
        shortTitle
        title
        shortIntro {
          value
        }
        intro {
          value
        }
      }
      ... on TargetGroupRecord {
        slug
        id
        shortTitle
        title
        shortIntro {
          value
        }
        intro {
          value
        }
        parent {
          slug
        }
      }
      ... on ArticleRecord {
        introImage {
          responsiveImage(
            imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "100", h: "100", q: "75" }
          ) {
            alt
            height
            src
            title
            width
          }
        }
        date
        tag {
          id
          title
        }
        slug
        id
        shortTitle
        title
        shortIntro {
          value
        }
        intro {
          value
        }
      }
      ... on ServiceRecord {
        slug
        id
        shortTitle
        title
        shortIntro {
          value
        }
        intro {
          value
        }
        parent {
          slug
        }
      }
    }
    enableBackground
  }
`;

export const REVIEW_SECTION_QUERY = gql`
  fragment ReviewSectionQuery on ReviewSectionRecord {
    id
    title
    reviews {
      id
      name
      jobTitle
      quote
      rating
      image {
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "72", h: "72", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
    }
  }
`;

export const EXPERT_SECTION_QUERY = gql`
  fragment ExpertSectionQuery on ExpertSectionRecord {
    id
    expert {
      id
      name
      jobTitle
      quote
      bio {
        value
      }
      image {
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "400", h: "400", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
    }
    enableBackground
  }
`;

export const SUBNAVIGATION_SECTION_QUERY = gql`
  fragment SubnavigationSectionQuery on SubnavigationSectionRecord {
    id
    title
    description {
      value
      links {
        __typename
        ... on ReusableTextRecord {
          id
          content
        }
        ... on ArticleRecord {
          id
          slug
        }
        ... on AccountanciesOverviewRecord {
          id
          __typename
        }
        ... on AccountancyRecord {
          id
          slug
        }
        ... on ArticlesOverviewRecord {
          id
          slug
        }
        ... on AuditPageRecord {
          id
          slug
        }
        ... on ContactRecord {
          id
          slug
        }
        ... on ExpertiseRecord {
          id
          slug
        }
        ... on ExpertisesOverviewRecord {
          id
          slug
        }
        ... on ForWhoOverviewRecord {
          id
          slug
        }
        ... on HomepageRecord {
          id
          slug
        }
        ... on JobListingOverviewRecord {
          id
          slug
        }
        ... on NearbyOverviewRecord {
          id
          slug
        }
        ... on OfficeRecord {
          id
          slug
          parent {
            slug
            parent {
              slug
              parent {
                slug
                parent {
                  slug
                }
              }
            }
          }
        }
        ... on PageRecord {
          id
          slug
        }
        ... on ServiceRecord {
          id
          slug
          parent {
            slug
          }
        }
        ... on ServicesOverviewRecord {
          id
          slug
        }
        ... on TaxesOverviewRecord {
          id
          slug
        }
        ... on TargetGroupRecord {
          id
          slug
          parent {
            slug
          }
        }
      }
    }
    layout
    items {
      id
      subpage {
        __typename
        ... on PageRecord {
          id
          title
          shortTitle
          slug
        }
        ... on ServiceRecord {
          id
          title
          shortTitle
          slug
          parent {
            slug
          }
        }
        ... on HomepageRecord {
          id
          title
          slug
        }
        ... on ServicesOverviewRecord {
          id
          title
          shortTitle
          slug
        }
        ... on OfficeRecord {
          id
          title
          shortTitle
          slug
          parent {
            slug
            parent {
              slug
              parent {
                slug
                parent {
                  slug
                }
              }
            }
          }
        }
        ... on NearbyOverviewRecord {
          id
          title
          shortTitle
          slug
        }
        ... on ExpertiseRecord {
          id
          title
          shortTitle
          slug
        }
        ... on ArticleRecord {
          id
          title
          shortArticleTitle: shortTitle
          slug
        }
        ... on ArticlesOverviewRecord {
          id
          title
          shortTitle
          slug
        }
        ... on MunicipalityRecord {
          id
          title
          slug
        }
        ... on ContactRecord {
          id
          title
          shortTitle
          slug
        }
        ... on ExpertisesOverviewRecord {
          id
          title
          shortTitle
          slug
        }
        ... on JobListingOverviewRecord {
          id
          title
          shortTitle
          slug
        }
        ... on AuditPageRecord {
          id
          title
          shortTitle
          slug
          parent {
            slug
          }
        }
        ... on ForWhoOverviewRecord {
          id
          title
          shortTitle
          slug
        }
        ... on TaxesOverviewRecord {
          id
          title
          shortTitle
          slug
        }
        ... on AccountanciesOverviewRecord {
          id
          title
          shortTitle
          slug
        }
        ... on TargetGroupRecord {
          id
          title
          shortTitle
          slug
          parent {
            slug
          }
        }
      }
      icon {
        id
        iconName
      }
    }
  }
`;

export const HUBSPOT_FORM_SECTION_QUERY = gql`
  fragment HubspotFormSectionQuery on HubspotFormSectionRecord {
    id
    formId
    portalId
  }
`;

export const SECTIONS_WRAPPER_QUERY = gql`
  ${RICHTEXT_SECTION_QUERY}
  ${ACCORDION_SECTION_QUERY}
  ${CTA_SECTION_QUERY}
  ${PEOPLE_SECTION_QUERY}
  ${CAROUSEL_SECTION_QUERY}
  ${QUOTE_CAROUSEL_SECTION_QUERY}
  ${CONTACT_FORM_SECTION_QUERY}
  ${AIRTABLE_FORM_SECTION_QUERY}
  ${CAMPAIGN_FORM_SECTION_QUERY}
  ${GRID_SECTION_QUERY}
  ${TEXT_MEDIA_COLUMN_SECTION_QUERY}
  ${GALLERY_SECTION_QUERY}
  ${EMBED_SECTION_QUERY}
  ${HIGHLIGHTS_SECTION_QUERY}
  ${SUBNAVIGATION_SECTION_QUERY}
  ${HIGHLIGHTED_JOBS_SECTION_QUERY}
  ${LATEST_NEWS_SECTION_QUERY}
  ${REVIEW_SECTION_QUERY}
  ${EXPERT_SECTION_QUERY}
  ${HUBSPOT_FORM_SECTION_QUERY}
  fragment SectionWrapperQuery on SectionWrapperRecord {
    sections {
      __typename
      ... on RichTextSectionRecord {
        ...RichtextSectionQuery
      }
      ... on AccordionSectionRecord {
        ...AccordionSectionQuery
      }
      ... on CtaSectionRecord {
        ...CtaSectionQuery
      }
      ... on PeopleSectionRecord {
        ...PeopleSectionQuery
      }
      ... on CarouselSectionRecord {
        ...CarouselSectionQuery
      }
      ... on QuoteCarouselSectionRecord {
        ...QuoteCarouselSectionQuery
      }
      ... on ContactFormSectionRecord {
        ...ContactFormSectionQuery
      }
      ... on AirtableFormSectionRecord {
        ...AirtableFormSectionQuery
      }
      ... on CampaignFormSectionRecord {
        ...CampaignFormSectionQuery
      }
      ... on GridSectionRecord {
        ...GridSectionQuery
      }
      ... on TextMediaColumnSectionRecord {
        ...TextMediaColumnSectionQuery
      }
      ... on GallerySectionRecord {
        ...GallerySectionQuery
      }
      ... on EmbedSectionRecord {
        ...EmbedSectionQuery
      }
      ... on HighlightsSectionRecord {
        ...HighlightsSectionQuery
      }
      ... on HighlightedJobsSectionRecord {
        ...HighlightedJobsSectionQuery
      }
      ... on SubnavigationSectionRecord {
        ...SubnavigationSectionQuery
      }
      ... on LatestNewsSectionRecord {
        ...LatestNewsSectionQuery
      }
      ... on ReviewSectionRecord {
        ...ReviewSectionQuery
      }
      ... on ExpertSectionRecord {
        ...ExpertSectionQuery
      }
      ... on HubspotFormSectionRecord {
        ...HubspotFormSectionQuery
      }
      ... on SharedSectionItemRecord {
        id
        sharedSection {
          section {
            __typename
            ... on RichTextSectionRecord {
              ...RichtextSectionQuery
            }
            ... on AccordionSectionRecord {
              ...AccordionSectionQuery
            }
            ... on CtaSectionRecord {
              ...CtaSectionQuery
            }
            ... on PeopleSectionRecord {
              ...PeopleSectionQuery
            }
            ... on CarouselSectionRecord {
              ...CarouselSectionQuery
            }
            ... on QuoteCarouselSectionRecord {
              ...QuoteCarouselSectionQuery
            }
            ... on ContactFormSectionRecord {
              ...ContactFormSectionQuery
            }
            ... on AirtableFormSectionRecord {
              ...AirtableFormSectionQuery
            }
            ... on CampaignFormSectionRecord {
              ...CampaignFormSectionQuery
            }
            ... on GridSectionRecord {
              ...GridSectionQuery
            }
            ... on HubspotFormSectionRecord {
              ...HubspotFormSectionQuery
            }
            ... on TextMediaColumnSectionRecord {
              ...TextMediaColumnSectionQuery
            }
            ... on GallerySectionRecord {
              ...GallerySectionQuery
            }
            ... on EmbedSectionRecord {
              ...EmbedSectionQuery
            }
            ... on HighlightsSectionRecord {
              ...HighlightsSectionQuery
            }
            # Note: HighlightedJobsSection not available in SharedSection yet
            # Add it to SharedSection model in DatoCMS if you need reusable highlighted job sections
            ... on LatestNewsSectionRecord {
              ...LatestNewsSectionQuery
            }
          }
        }
      }
    }
  }
`;
